/**
 * bd-cookie-banner.js
 *
 * Drives the cookie/tracking consent notice rendered by
 * snippets/bd-cookie-banner.liquid (SSR after </main>, before HubSpot).
 *
 * The visitor's choice is written to Shopify's Customer Privacy API so it
 * reaches Shopify-managed surfaces (web pixels, marketing audiences,
 * checkout), and mirrored in localStorage + a first-party cookie so the
 * banner does not reappear if that API is slow or blocked.
 *
 * Consent signal mapping:
 *   Accept all      -> analytics/marketing/preferences granted, sale_of_data granted
 *   Essential only  -> all four denied (sale_of_data: false === opted out of
 *                      data sale/sharing, per the Customer Privacy API)
 *
 * Reopening: any link to "#cookie-preferences", any element carrying
 * [data-bd-cookie-preferences], or window.BDCookieBanner.show().
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'bd_cookie_consent_v1';
  var COOKIE_MAX_AGE = 31536000;
  var banner = document.querySelector('[data-bd-cookie-banner]');
  if (!banner) return;

  var designMode = !!(window.Shopify && window.Shopify.designMode);

  /* ---------- local + cookie mirror of the decision ------------------- */

  function readCookie() {
    try {
      var match = document.cookie.match(new RegExp('(?:^|; )' + STORAGE_KEY + '=([^;]*)'));
      return match ? decodeURIComponent(match[1]) : null;
    } catch (e) {
      return null;
    }
  }

  function readStored() {
    try {
      var value = window.localStorage.getItem(STORAGE_KEY);
      if (value) return value;
    } catch (e) {
      /* Private mode or storage disabled — fall through to the cookie. */
    }
    return readCookie();
  }

  function writeStored(value) {
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch (e) {
      /* Private mode or storage disabled — the cookie + API still stand. */
    }
    try {
      document.cookie = STORAGE_KEY + '=' + encodeURIComponent(value) +
        '; Path=/; Max-Age=' + COOKIE_MAX_AGE + '; SameSite=Lax';
    } catch (e) {
      /* Cookie blocked — localStorage + API still stand. */
    }
  }

  /* ---------- show / hide -------------------------------------------- */

  function show() {
    banner.setAttribute('data-open', 'true');
    document.documentElement.classList.remove('bd-cookie-decided');
    document.body.classList.add('bd-cookie-open');
    // Publish the measured height so the PDP sticky add-to-cart bar can sit
    // above the notice instead of covering it.
    document.documentElement.style.setProperty(
      '--bd-cookie-height',
      banner.offsetHeight + 'px'
    );
  }

  function hide() {
    banner.setAttribute('data-open', 'false');
    document.documentElement.classList.add('bd-cookie-decided');
    document.body.classList.remove('bd-cookie-open');
    document.documentElement.style.removeProperty('--bd-cookie-height');
  }

  // Wrapping to a second line on resize changes the height the sticky bar needs.
  window.addEventListener('resize', function () {
    if (banner.getAttribute('data-open') !== 'true') return;
    document.documentElement.style.setProperty(
      '--bd-cookie-height',
      banner.offsetHeight + 'px'
    );
  });

  /* ---------- Shopify Customer Privacy API --------------------------- */

  function privacyApi() {
    return window.Shopify && window.Shopify.customerPrivacy;
  }

  /**
   * True when the visitor has actively granted or denied consent already.
   * currentVisitorConsent() reports 'yes' / 'no' / '' (undecided) per signal.
   */
  function decisionRecorded() {
    var api = privacyApi();
    if (!api || typeof api.currentVisitorConsent !== 'function') return false;

    var consent = api.currentVisitorConsent() || {};
    return ['analytics', 'marketing', 'preferences', 'sale_of_data'].some(function (key) {
      return consent[key] === 'yes' || consent[key] === 'no';
    });
  }

  function applyConsent(granted) {
    var api = privacyApi();
    if (!api || typeof api.setTrackingConsent !== 'function') return;

    api.setTrackingConsent(
      {
        analytics: granted,
        marketing: granted,
        preferences: granted,
        sale_of_data: granted
      },
      function (result) {
        if (result && result.error) {
          // Keep the local decision: re-prompting on every page would be worse
          // than one failed hand-off, and Shopify retries on the next call.
          console.warn('[bd-cookie-banner] setTrackingConsent failed:', result.error);
        }
      }
    );
  }

  function loadPrivacyApi(callback) {
    if (privacyApi()) {
      callback(null);
      return;
    }

    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
      callback(new Error('Shopify.loadFeatures unavailable'));
      return;
    }

    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      function (error) {
        callback(error || null);
      }
    );
  }

  /* ---------- wiring -------------------------------------------------- */

  function record(granted) {
    applyConsent(granted);
    if (!designMode) writeStored(granted ? 'accepted' : 'essential');
    hide();
    document.dispatchEvent(new CustomEvent('bd:cookie-consent', {
      detail: { granted: !!granted }
    }));
  }

  banner.addEventListener('click', function (event) {
    if (!event.target || !event.target.closest) return;
    var trigger = event.target.closest('[data-bd-cookie-action]');
    if (!trigger) return;
    record(trigger.getAttribute('data-bd-cookie-action') === 'accept');
  });

  // Let visitors reopen the notice to change their mind.
  document.addEventListener('click', function (event) {
    if (!event.target || !event.target.closest) return;
    var trigger = event.target.closest('[data-bd-cookie-preferences], a[href="#cookie-preferences"]');
    if (!trigger) return;
    event.preventDefault();
    show();
  });

  window.BDCookieBanner = { show: show, hide: hide };

  /* ---------- decide whether to prompt -------------------------------- */

  // In the theme editor, always show it so it can be seen and styled.
  if (designMode) {
    show();
    return;
  }

  // Banner is already in the HTML. Hide immediately when a prior choice exists;
  // do not wait for the privacy API or any third-party script.
  if (readStored()) {
    hide();
    return;
  }

  document.body.classList.add('bd-cookie-open');
  document.documentElement.style.setProperty(
    '--bd-cookie-height',
    banner.offsetHeight + 'px'
  );

  loadPrivacyApi(function (error) {
    if (error || !decisionRecorded()) return;
    var consent = privacyApi().currentVisitorConsent() || {};
    var granted = ['analytics', 'marketing', 'preferences', 'sale_of_data'].some(function (key) {
      return consent[key] === 'yes';
    });
    writeStored(granted ? 'accepted' : 'essential');
    hide();
  });
})();
